import { object, string } from 'yup';
import { useFormik } from 'formik';
import { useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import useMeContext from '~/modules/me/useMeContext';
import { filterSummarizeColumnOptions } from '~/modules/billing-invoicing/common/util';

export const buildValidationSchema = ({ intl }) =>
  object().shape({
    description: string().max(
      255,
      intl.formatMessage({
        id: 'billDetails.validations.descriptionExceedsMaxLength'
      })
    )
  });

export const useInitialState = (
  isPsaPrpCustomLineItemParityFlagEnabled = false
) => ({
  project: null,
  description: '',
  unit: isPsaPrpCustomLineItemParityFlagEnabled ? 1 : null,
  label: '',
  rate: isPsaPrpCustomLineItemParityFlagEnabled ? 0 : null,
  amount: 0
});

export const useOnSubmit = ({
  putLineItemForBill,
  billDetails,
  isPsaPrpCustomLineItemParityFlagEnabled = false
}) =>
  useCallback(
    async values => {
      const billId = billDetails.id;
      const filteredSummarizeColumnOptions = filterSummarizeColumnOptions(
        billDetails.summarizeColumnOptions
      );
      const {
        amount,
        project,
        description,
        rate,
        quantity,
        unit,
        label
      } = values;

      await putLineItemForBill(
        {
          billId,
          lineItem: {
            amount,
            projectUri:
              project && project.id !== 'none-option' ? project.id : null,
            description,
            rate,
            unitLabel: isPsaPrpCustomLineItemParityFlagEnabled
              ? label
              : undefined,
            quantity: isPsaPrpCustomLineItemParityFlagEnabled ? unit : quantity
          }
        },
        billId,
        filteredSummarizeColumnOptions
      );
    },
    [
      billDetails.id,
      billDetails.summarizeColumnOptions,
      isPsaPrpCustomLineItemParityFlagEnabled,
      putLineItemForBill
    ]
  );

export const usePutCustomLineItemFormState = ({
  putLineItemForBill,
  billDetails
}) => {
  const {
    featureFlags: { isPsaPrpCustomLineItemParityFlagEnabled }
  } = useMeContext();
  const intl = useIntl();
  const initialValues = useInitialState(
    isPsaPrpCustomLineItemParityFlagEnabled
  );
  const onSubmit = useOnSubmit({
    putLineItemForBill,
    billDetails,
    isPsaPrpCustomLineItemParityFlagEnabled
  });
  const validationSchema = useMemo(() => buildValidationSchema({ intl }), [
    intl
  ]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit,
    validationSchema
  });

  return formik;
};

export default usePutCustomLineItemFormState;
